import { Fragment, useState, useEffect } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import ChevronIcon from '../icons/ChevronIcon'
import CheckIcon from '../icons/CheckIcon'
import classNames from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import { SelectOption } from './MinimalSelect'
import { cva } from 'class-variance-authority'

type PropTypes = {
  placeholder?: string
  options: SelectOption[]
  defaultValue?: any
  onChange?: (option: SelectOption) => void
  onSearchChange?: (query: string) => void
  error?: string,
}

export default function SearchableSelect ({ placeholder, options, defaultValue, onChange, onSearchChange, error } : PropTypes) {
  const [selected, setSelected] = useState<SelectOption | undefined>(options.find((option) => option.value === defaultValue))

  const [showPrefix, setShowPrefix] = useState(true)

  useEffect(() => {
    setSelected(options.find((option) => option.value === defaultValue))
  }, [defaultValue, options])

  const debouncedSearchChange = useDebouncedCallback(
    (value) => onSearchChange?.(value),
    1000,
  )

  const inputStyles = cva(
    [
      'w-full py-3 px-3 peer rounded text-gray-700 text-base leading-6 font-medium placeholder-gray-600 border-gray-400 border outline-none transition-all',
      'focus:ring-2 focus:ring-green-900 focus:border-gray-400',
    ],
    {
      variants: {
        error: {
          true: 'text-red-700 border-red-700',
        },
        hasPrefix: {
          true: 'pl-12',
        },
      },
    },
  )

  return (
    <div className="flex flex-col items-start">
      <div className="w-full">
        <Combobox
          value={selected}
          defaultValue={defaultValue}
          onChange={(value) => {
            setSelected(value)
            onChange?.(value)

            // Re-shown the prefix icon when the user selects an option
            setShowPrefix(true)
          }}
        >
          <div className="relative">
            <div>
              {
                showPrefix
                && (
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                    {selected?.prefix}
                  </div>
                )
              }
              <Combobox.Input
                placeholder={placeholder}
                className={classNames(inputStyles({ error: Boolean(error), hasPrefix: Boolean(selected?.prefix && showPrefix) }))}
                displayValue={(option: SelectOption) => option?.label ?? '-'}
                onChange={(event) => {
                  debouncedSearchChange(event.target.value)

                  // Hide the prefix icon when the user is searching or selecting to more clearly indicate the
                  // input is a searchbox now
                  setShowPrefix(false)
                }}
                // Re-shown the prefix icon when the user clicks away
                onBlur={() => setShowPrefix(true)}
              />
              <Combobox.Button
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                // Hide the prefix icon when the user is searching or selecting to more clearly indicate the
                // input is a searchbox now
                onClick={() => setShowPrefix(false)}
              >
                <ChevronIcon
                  className="h-3 w-3"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options className="absolute mt-1 w-full z-10 rounded p-[5px] bg-gray-900 text-base shadow-lg ring-1 ring-gray-900 focus:outline-none sm:text-sm">
                {
                  options.length === 0
                    ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-white">
                        Nothing found.
                      </div>
                    )
                    : (
                      options.map((option) => {
                        const isSelected = option.value === selected?.value
                        return (
                          <Combobox.Option
                            key={option.value}
                            className={({ active }) =>
                              classNames(
                                active ? 'bg-gray-700' : '',
                                'rounded cursor-default select-none py-1.5 pl-2.5 pr-1 text-white',
                              )
                            }
                            value={option}
                          >
                            {({ selected: _ }) => (
                              <div className="flex flex-row items-center">
                                <span className={classNames(isSelected ? 'font-semibold' : 'font-normal', 'truncate flex flex-row items-center gap-2')}>
                                  {option.prefix}
                                  {option.label}
                                </span>
                                {
                                  isSelected
                                    ? (
                                      <span className="text-white">
                                        <CheckIcon
                                          className="h-5 w-5 ml-2"
                                        />
                                      </span>
                                    )
                                    : null
                                }
                              </div>
                            )}
                          </Combobox.Option>
                        )
                      })
                    )
                }
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      </div>
      {
        error
        && <p className="text-red-700 text-[13px] leading-[15.6px]">
          {error}
        </p>
      }
    </div>
  )
}
